import type { DividerDesignTokens } from '@primeuix/themes/types/divider'
import type { MessageDesignTokens } from '@primeuix/themes/types/message'
import type { ProgressBarDesignTokens } from '@primeuix/themes/types/progressbar'
import type { SkeletonDesignTokens } from '@primeuix/themes/types/skeleton'
import type { ToastDesignTokens } from '@primeuix/themes/types/toast'

import { bothSchemes } from '../utils'

export const messageConfig = (): MessageDesignTokens => ({
  root: {
    borderRadius: 'var(--radius-l)',
    borderWidth: '0',
    transitionDuration: '.2s',
  },
  content: {
    padding: 'var(--space-6) var(--space-7)',
    gap: 'var(--space-4)',
    sm: { padding: 'var(--space-4) var(--space-5)' },
  },
  text: {
    fontSize: '13px',
    fontWeight: 'var(--fw-medium)',
  },
  icon: { size: '20px' },
  colorScheme: bothSchemes({
    info: {
      background: 'rgb(1 124 250 / 12%)',
      borderColor: 'transparent',
      color: 'var(--info-500)',
      shadow: 'none',
    },
    success: {
      background: 'rgb(14 169 0 / 12%)',
      borderColor: 'transparent',
      color: 'var(--success-500)',
      shadow: 'none',
    },
    warn: {
      background: 'rgb(255 191 26 / 20%)',
      borderColor: 'transparent',
      color: 'var(--text-color)',
      shadow: 'none',
    },
    error: {
      background: 'rgb(218 33 40 / 12%)',
      borderColor: 'transparent',
      color: 'var(--primary-500)',
      shadow: 'none',
    },
    secondary: {
      background: 'var(--surface-muted)',
      borderColor: 'transparent',
      color: 'var(--text-color)',
      shadow: 'none',
    },
    contrast: {
      background: 'var(--surface-inverse)',
      borderColor: 'transparent',
      color: 'var(--text-color-inverse)',
      shadow: 'none',
    },
  }),
})

export const toastConfig = (): ToastDesignTokens => ({
  root: {
    width: 'calc(100% - 32px)',
    borderRadius: 'var(--radius-xl)',
    borderWidth: '0',
  },
  content: {
    padding: 'var(--space-7)',
    gap: 'var(--space-5)',
  },
  text: { gap: 'var(--space-2)' },
  summary: { fontWeight: 'var(--fw-bold)', fontSize: '14px' },
  detail: { fontWeight: 'var(--fw-medium)', fontSize: '13px' },
  icon: { size: '24px' },
  closeButton: {
    width: '24px',
    height: '24px',
    borderRadius: 'var(--radius-round)',
    focusRing: { width: '0', style: 'none', offset: '0' },
  },
  colorScheme: bothSchemes({
    success: {
      background: 'var(--secondary-950)',
      borderColor: 'transparent',
      color: 'var(--white)',
      detailColor: 'var(--white)',
      shadow: 'var(--shadow-card)',
      closeButton: { hoverBackground: 'rgb(255 255 255 / 12%)' },
    },
    info: {
      background: 'var(--secondary-950)',
      borderColor: 'transparent',
      color: 'var(--white)',
      detailColor: 'var(--white)',
      shadow: 'var(--shadow-card)',
      closeButton: { hoverBackground: 'rgb(255 255 255 / 12%)' },
    },
    warn: {
      background: 'var(--accent-500)',
      borderColor: 'transparent',
      color: 'var(--accent-surface-color)',
      detailColor: 'var(--accent-surface-color)',
      shadow: 'var(--shadow-card)',
      closeButton: { hoverBackground: 'rgb(0 0 0 / 8%)' },
    },
    error: {
      background: 'var(--primary-500)',
      borderColor: 'transparent',
      color: 'var(--white)',
      detailColor: 'var(--white)',
      shadow: 'var(--shadow-card)',
      closeButton: { hoverBackground: 'rgb(255 255 255 / 12%)' },
    },
  }),
})

export const skeletonConfig = (): SkeletonDesignTokens => ({
  root: {
    borderRadius: 'var(--radius-l)',
  },
  colorScheme: bothSchemes({
    root: {
      background: 'var(--surface-muted)',
      animationBackground: 'rgb(171 173 176 / 25%)',
    },
  }),
})

/** «До бесплатной доставки» / «До подарка» — прогресс жёлтым. */
export const progressBarConfig = (): ProgressBarDesignTokens => ({
  root: {
    borderRadius: 'var(--radius-pill)',
    height: '6px',
  },
  label: {
    fontSize: '10px',
    fontWeight: 'var(--fw-bold)',
  },
  colorScheme: bothSchemes({
    root: { background: 'var(--secondary-100)' },
    value: { background: 'var(--accent-500)' },
    label: { color: 'var(--text-color)' },
  }),
})

export const dividerConfig = (): DividerDesignTokens => ({
  horizontal: {
    margin: 'var(--space-7) 0',
    content: { padding: '0 var(--space-5)' },
  },
  colorScheme: bothSchemes({
    root: { borderColor: 'var(--divider-color)' },
    content: {
      background: 'var(--surface-card)',
      color: 'var(--text-color-secondary)',
    },
  }),
})
